/**
 * Factory to create landing page components (events/memberships)
 *
 * This eliminates duplication between event/membership pages.
 * Each page just passes in the hook, adapter, and optional auth wrapper.
 *
 * Theme responsibilities live HERE (above the auth wrapper):
 * - Memoizes themeConfig so downstream providers get a stable reference
 * - Calls useSetPageThemeConfig to override the SSR initialThemeConfig
 *   (must be at this level so it runs even while an auth wrapper gates children)
 *
 * Color mode is handled by the ThemeProvider -> ColorModeProvider chain
 * via forceColorMode in the theme config. No manual setColorMode needed.
 */
import { ComponentType, ReactNode, useMemo } from "react";
import { common } from "@shared/constants";
import { BasketServiceProvider } from "@shared/modules/basket/context/BasketServiceContext";
import { SeatingStateProvider } from "@shared/modules/seating/contexts/useSeatingState";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { useSetPageThemeConfig } from "@shared/contexts/PageThemeConfigContext/PageThemeConfigContext";
import { useSetLayoutPreset } from "@shared/contexts/LayoutPresetContext/LayoutPresetContext";
import {
  type LayoutThemeConfig,
  type LayoutPreset,
  DEFAULT_LAYOUT_PRESET,
} from "@shared/config/layout.config";
import type { PageData } from "../adapters/types";
import { resolveLayout } from "./resolveLayout";

interface CreateLandingPageOptions<TProps, TState, TAuthProps = unknown> {
  /** Hook that fetches/manages page state */
  usePageHook: (props: TProps) => TState;
  /** Adapter that transforms state to PageData */
  adapter: (state: TState) => PageData;
  /** Optional authorization wrapper component */
  AuthorizationWrapper?: ComponentType<TAuthProps & { children: ReactNode }>;
  /** Extract auth props from state */
  getAuthProps?: (state: TState, pageData: PageData) => TAuthProps;
  /** Get unique key for SeatingStateProvider */
  getStateKey?: (state: TState) => string | number | undefined;
  /** Get theme config from page state -- use toThemeConfig(state.venueBrandingConfig) */
  getThemeConfig?: (
    state: TState,
    pageData: PageData
  ) => LayoutThemeConfig | undefined;
  /** Get layout preset for the page (classic/hero) */
  getLayoutPreset?: (
    state: TState,
    pageData: PageData
  ) => LayoutPreset | null | undefined;
}

export function createLandingPageComponent<
  TProps,
  TState,
  TAuthProps = unknown
>({
  usePageHook,
  adapter,
  AuthorizationWrapper,
  getAuthProps,
  getStateKey,
  getThemeConfig,
  getLayoutPreset,
}: CreateLandingPageOptions<TProps, TState, TAuthProps>) {
  return function LandingPageComponent(props: TProps) {
    const state = usePageHook(props);

    const pageData = adapter(state);

    const stateKey = getStateKey?.(state) ?? "default";
    const rawThemeConfig = getThemeConfig?.(state, pageData);

    // Memoize the themeConfig so downstream consumers get a stable reference.
    // getThemeConfig returns a new object every render; JSON key comparison
    // ensures we only produce a new reference when the values actually change.
    const themeConfigKey = rawThemeConfig
      ? JSON.stringify(rawThemeConfig)
      : undefined;
    const themeConfig = useMemo(() => rawThemeConfig, [themeConfigKey]);

    // Applied at this level (above the auth wrapper) so the correct theme
    // is active even while a password modal is blocking children.
    useSetPageThemeConfig(themeConfig);

    // --- Layout preset (global context) ---
    // Applied at this level (above the auth wrapper) so the navbar gets the
    // correct preset immediately, not after EventAuthorization releases children.
    const layoutPreset =
      getLayoutPreset?.(state, pageData) ?? DEFAULT_LAYOUT_PRESET;
    useSetLayoutPreset(layoutPreset);

    const finalPageData: PageData = layoutPreset
      ? { ...pageData, layoutPreset }
      : pageData;

    const Layout = resolveLayout(finalPageData);

    const content = (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      <BasketServiceProvider basketService={usePublicBasket as any}>
        <SeatingStateProvider
          isOnSeatingMapPage={false}
          basketType={common.DataProvider.Public}
          key={`assigned-seating-state-${stateKey}`}
        >
          <Layout data={finalPageData} />
        </SeatingStateProvider>
      </BasketServiceProvider>
    );

    if (AuthorizationWrapper && getAuthProps) {
      const authProps = getAuthProps(state, pageData);
      return (
        <AuthorizationWrapper {...authProps}>{content}</AuthorizationWrapper>
      );
    }

    return content;
  };
}

// Re-export for backwards compatibility during migration
export { createLandingPageComponent as createDetailPageComponent };
